import { useState } from "react";
import { useMasters } from "../../contexts/MastersContext";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Calendar, DollarSign, MapPin, FileText, Briefcase, Users, Clock, X } from "lucide-react";

// Move InputField component outside to prevent re-rendering issues
const InputField = ({ label, name, type = "text", placeholder, icon: Icon, children, className = "", required = false, formData, handleChange, errors, isFieldDisabled }) => (
  <div className={`relative ${className}`}>
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
      {Icon && <Icon size={16} className={`${errors[name] ? 'text-red-500' : 'text-blue-500'}`} />}
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative group">
      {children ? children : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-md transition-all duration-200 font-medium
            ${errors[name] 
              ? "border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-600 focus:ring-red-200" 
              : "border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-100"
            }
            ${isFieldDisabled(name) ? "opacity-60 cursor-not-allowed" : "hover:border-gray-300 dark:hover:border-gray-500"}
            focus:outline-none focus:ring-4 focus:ring-opacity-20
            text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
          `}
          disabled={isFieldDisabled(name)}
          required={required}
          min={0}
        />
      )}
      {Icon && !children && (
        <Icon size={18} className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${
          errors[name] ? 'text-red-500' : 'text-gray-400 group-focus-within:text-blue-500'
        }`} />
      )}
    </div>
    {errors[name] && (
      <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
        <X size={14} className="text-red-500" />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const CampaignForm = ({ closeModal, onSubmit, initialValues, mode = "Add" }) => {
  const { workplaces, loading, forms, error } = useMasters();

  // Convert workplace name to ID if needed
  let workplaceId = initialValues.workplace;
  if (workplaceId && workplaces && isNaN(Number(workplaceId))) {
    const found = workplaces.find(w => w.name === workplaceId);
    workplaceId = found ? found.id : "";
  }

  const [formData, setFormData] = useState({
    campaign_name: initialValues.campaign_name || "",
    position: initialValues.position || "",
    no_of_positions: initialValues.no_of_positions || "",
    job_description: initialValues.job_description || "",
    ctc: initialValues.ctc || "",
    from_date: initialValues.from_date || "",
    to_date: initialValues.to_date || "",
    justification: initialValues.justification || "",
    workplace: workplaceId || "",
    // application_form_id: initialValues.application_form_id || "",
    form_id: initialValues.form_id || "",
    submission_id: initialValues.submission_id || "",
    status: initialValues.status || "Active",
  });

  // console.log("initial values:", initialValues)

  const [errors, setErrors] = useState({});
  const [loadingState, setLoadingState] = useState(false); 

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent numbers and special characters in text fields
    if (name === "campaign_name" || name === "position") {
      // Only allow letters and spaces
      const textOnlyRegex = /^[A-Za-z\s]*$/;
      if (value && !textOnlyRegex.test(value)) {
        return; // Don't update the field if it contains numbers or special characters
      }
    }

    // Allow only positive numbers for no_of_positions
    if (name === "no_of_positions") {
      // Only allow digits
      const numberOnlyRegex = /^\d*$/;
      if (value && !numberOnlyRegex.test(value)) {
        return; // Don't update the field if it contains non-digit characters
      }
    }

    if (name === "form") {
      const selectedForm = forms.find((form) => form.name === value);
      setFormData((prev) => ({ ...prev, form: selectedForm?.id || "", application_form_id: selectedForm?.id || "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  
    // Clear error for this field when user starts typing
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    
    // Real-time validation for specific fields
    const newErrors = { ...errors };
    
    // Validate campaign name
    if (name === "campaign_name") {
      if (!value.trim()) {
        newErrors.campaign_name = "Campaign name is required.";
      } else {
        delete newErrors.campaign_name;
      }
    }
    
    // Validate position
    if (name === "position") {
      if (!value.trim()) {
        newErrors.position = "Position title is required.";
      } else {
        delete newErrors.position;
      }
    }
    
    // Validate number of positions
    if (name === "no_of_positions") {
      if (!value) {
        newErrors.no_of_positions = "Number of positions is required.";
      } else if (parseInt(value) <= 0) {
        newErrors.no_of_positions = "Number of positions must be a positive number.";
      } else {
        delete newErrors.no_of_positions;
      }
    }
    
    // Validate CTC
    if (name === "ctc") {
      if (!value || value <= 0) {
        newErrors.ctc = "CTC must be a positive number.";
      } else {
        delete newErrors.ctc;
      }
    }
    
    // Validate dates
    if (name === "from_date") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fromDate = new Date(value);
      
      if (!value) {
        newErrors.from_date = "From date is required.";
      } else if (fromDate < today) {
        newErrors.from_date = "Start date must be today or later.";
      } else {
        delete newErrors.from_date;
      }
      
      // Also validate to_date if it exists
      if (formData.to_date && new Date(formData.to_date) < fromDate) {
        newErrors.to_date = "End date must be after or equal to the start date.";
      } else if (formData.to_date) {
        delete newErrors.to_date;
      }
    }
    
    if (name === "to_date") {
      const fromDate = new Date(formData.from_date);
      const toDate = new Date(value);
      
      if (!value) {
        newErrors.to_date = "To date is required.";
      } else if (toDate < fromDate) {
        newErrors.to_date = "End date must be after or equal to the start date.";
      } else {
        delete newErrors.to_date;
      }
    }
    
    // Validate form selection
    // if (name === "application_form_id") {
    //   if (!value) {
    //     newErrors.application_form_id = "Form is required.";
    //   } else {
    //     delete newErrors.application_form_id;
    //   }
    // }
    
    // Validate workplace selection
    if (name === "workplace") {
      if (!value) {
        newErrors.workplace = "Workplace is required.";
      } else {
        delete newErrors.workplace;
      }
    }
    
    // Validate status
    if (name === "status") {
      if (!value) {
        newErrors.status = "Status is required.";
      } else {
        delete newErrors.status;
      }
    }
    
    setErrors(newErrors);
  };
  
  const validateForm = () => {
    const validationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fromDate = new Date(formData.from_date);
    const toDate = new Date(formData.to_date);

    // Campaign name validation
    if (!formData.campaign_name.trim()) {
      validationErrors.campaign_name = "Campaign name is required.";
    }

    // Position validation
    if (!formData.position.trim()) {
      validationErrors.position = "Position titleis required.";
    } 

    // Number of positions validation
    if (!formData.no_of_positions) {
      validationErrors.no_of_positions = "Number of positions is required.";
    } else if (parseInt(formData.no_of_positions) <= 0) {
      validationErrors.no_of_positions = "Number of positions must be a positive number.";
    }

    // Job description validation
    if (!formData.job_description.trim()) {
      validationErrors.job_description = "Job description is required.";
    }

    // CTC validation
    if (!formData.ctc || formData.ctc <= 0) {
      validationErrors.ctc = "CTC must be a positive number.";
    }

    // Date validations
    if (!formData.from_date) {
      validationErrors.from_date = "From date is required.";
    } else if (fromDate < today) {
      validationErrors.from_date = "Start date must be today or later.";
    }

    if (!formData.to_date) {
      validationErrors.to_date = "To date is required.";
    } else if (toDate < fromDate) {
      validationErrors.to_date = "End date must be after or equal to the start date.";
    }

    // Form validation
    // if (!formData.application_form_id) {
    //   validationErrors.application_form_id = "Form is required.";
    // }

    // Workplace validation
    if (!formData.workplace) {
      validationErrors.workplace = "Workplace is required.";
    }

    // Status validation
    if (!formData.status) {
      validationErrors.status = "Status is required.";
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoadingState(true)
  
    try {
      await onSubmit({
        ...formData,
        form: formData.form,
      });
      closeModal();
    } catch (error) {
      console.error("Submission Error:", error);
      if (error.errors) {
        setErrors(error.errors);
      }
    } finally { 
      setLoadingState(false);
    }
  };

  const isFieldDisabled = (fieldName) => {
    // Allow all fields to be editable in all modes
    return false; 
  };



  return (
    <div className="mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6 font-poppins">
        {/* Campaign Basic Info */}
        <div className=" dark:from-gray-800 dark:to-gray-850 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField 
              label="Campaign Name" 
              name="campaign_name" 
              placeholder="Enter campaign name"
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              // required
              isFieldDisabled={isFieldDisabled}
            />
            <InputField 
              label="Position Title" 
              name="position" 
              placeholder="Job position title"
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              // required
              isFieldDisabled={isFieldDisabled}
            />
            <InputField 
              label="No. Of Positions" 
              name="no_of_positions" 
              type="number"
              placeholder="e.g 1, 2"
              // required 
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              isFieldDisabled={isFieldDisabled}
            />
            {/* Status Field */}
            <InputField label="Status" name="status" required formData={formData} handleChange={handleChange} errors={errors} isFieldDisabled={isFieldDisabled}>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-md transition-all duration-200 font-medium
                  ${errors.status 
                    ? "border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-600 focus:ring-red-200" 
                    : "border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-100"
                  }
                  hover:border-gray-300 dark:hover:border-gray-500
                  focus:outline-none focus:ring-4 focus:ring-opacity-20
                  text-gray-900 dark:text-gray-100
                `}
                // required
              >
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Published">Published</option>
                <option value="Expired">Expired</option>
              </select>
            </InputField>
          </div>
        </div>

        {/* Job Description */}
        <div className=" dark:from-gray-800 dark:to-gray-850 rounded-md">
          <div className="relative">
            <ReactQuill
              theme="snow"
              value={formData.job_description}
              onChange={(content) => {
                setFormData((prev) => ({ ...prev, job_description: content }));
                
                // Real-time validation for job description
                const newErrors = { ...errors };
                if (!content.trim()) {
                  newErrors.job_description = "Job description is required.";
                } else {
                  delete newErrors.job_description;
                }
                setErrors(newErrors);
              }}
              className={`bg-white dark:bg-gray-800 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                errors.job_description ? "border-red-500" : "border-gray-200 dark:border-gray-600"
              }`}
              style={{
                minHeight: '120px'
              }}
            />
            {errors.job_description && (
              <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
                <X size={14} className="text-red-500" />
                <span>{errors.job_description}</span>
              </div>
            )}
          </div>
        </div>

        {/* Salary Range */}
        <div className="dark:from-gray-800 dark:to-gray-850 rounded-2xl ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField 
              label="CTC" 
              name="ctc" 
              type="number"
              placeholder="Enter CTC"
              // required 
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              isFieldDisabled={isFieldDisabled}
            />
          </div>
        </div>

        {/* Dates */}
        <div className="dark:from-gray-800 dark:to-gray-850 rounded-md ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField 
              label="From Date" 
              name="from_date" 
              type="date"
              // icon={Calendar}
              // required 
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              isFieldDisabled={isFieldDisabled}
            />
            
            <InputField 
              label="To Date" 
              name="to_date" 
              type="date"
              // icon={Clock}
              // required 
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              isFieldDisabled={isFieldDisabled}
            />
          </div>
        </div>

        {/* Form and Workplace */}
        <div className="dark:from-gray-800 dark:to-gray-850 rounded-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Workplace" name="workplace" required formData={formData} handleChange={handleChange} errors={errors} isFieldDisabled={isFieldDisabled}>
              {loading ? (
                <div className="flex items-center justify-center p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading workplaces...</span>
                </div>
              ) : error ? (
                <p className="text-red-500 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-xl">{error}</p>
              ) : (
                <select
                  name="workplace"
                  value={String(formData.workplace)}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-md transition-all duration-200 font-medium
                    ${errors.workplace 
                      ? "border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-600 focus:ring-red-200" 
                      : "border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-100"
                    }
                    hover:border-gray-300 dark:hover:border-gray-500
                    focus:outline-none focus:ring-4 focus:ring-opacity-20
                    text-gray-900 dark:text-gray-100
                  `}
                  // required
                  >
                  <option value="">Select Workplace</option>
                  {workplaces.map((place) => (
                    <option key={place.id} value={String(place.id)}>
                      {place.name}
                    </option>
                  ))}
                </select>
              )}
            </InputField>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end items-center gap-2 pt-2">
          <button
            type="button"
            onClick={closeModal}
            className="px-12 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={loadingState}
            className="flex items-center gap-1 bg-blue-600 font-medium hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:cursor-not-allowed"
          >
            {mode === "Add"
              ? "Create Campaign"
              : mode === "Convert"
                ? "Convert to Campaign"
                : "Update Campaign"}

            {loadingState && (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            )}  
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignForm;